import React, { useEffect, useCallback, useState, useContext, useReducer, useRef, useLayoutEffect } from 'react';
import { fabric } from 'fabric';
import { v4 } from 'uuid';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import LoopIcon from '@mui/icons-material/Loop';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { IconButton, Badge, Slider } from '@mui/material';
import ReactPlayer from 'react-player';
import CircleOutlined from '@mui/icons-material/CircleOutlined';
import Circle from '@mui/icons-material/Circle';
import RectangleOutlined from '@mui/icons-material/RectangleOutlined';
import HighlightAltOutlined from '@mui/icons-material/HighlightAltOutlined';
import PanToolOutlined from '@mui/icons-material/PanTool';
import Create from '@mui/icons-material/Create';
import TextFields from '@mui/icons-material/Title';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import CopyIcon from '@mui/icons-material/ContentCopy';
import PasteIcon from '@mui/icons-material/ContentPaste';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import { SketchPicker } from 'react-color';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

const DEFAULT_UI_ATTRS = {
    mode: 'selection',
    showAnnotation: true,
    shape: 'Rect',
    fontSize: 20,
    // color: 'DeepSkyBlue',
    // color: 'DodgerBlue',
    color: 'Coral',
    strokeWidth: 10,
    fontFamily: 'Times New Roman',
};
const DEFAULT_CANVAS_ATTRS = {
    uniformScaling: false,
    preserveObjectStacking: true,
    targetFindTolerance: 10,
    // note: currently, canvas group selection only the selection rect with shape bounds.
    // ref: https://github.com/fabricjs/fabric.js/issues/3773
    // So next best thing is to require user's selection rect to contain the entire object.
    // selectionFullyContained: true,
};
const DEFAULT_SELECTION_CONFIG = {
    perPixelSelection: false,
    selectionColor: 'Azure',
    hoverBoundingBox: true,
};
const FabricObjectDefaults = {
    // perPixelTargetFind: true,
    noScaleCache: false,
    strokeUniform: true,
};
const ANNOTATE_KEY_HOLD_MSEC = 500;

const baseAttrNames = ['fill', 'stroke', 'strokeWidth'];
const fabricObjAttrsLookup = {
    Rect: [...baseAttrNames, 'width', 'height'],
    Circle: [...baseAttrNames, 'radius'],
    Ellipse: [...baseAttrNames, 'rx', 'ry'],
    Textbox: [
        ...baseAttrNames,
        'text',
        'fontFamily',
        'fontSize',
        'width',
        'height',
    ],
    Path: [...baseAttrNames, 'path'],
    SVG: [], // placeholder
};

/**
 * Serialize the fabric object by extracting the attributes into a hash.
 * This happens when user draws a new element.
 * @param props
 * @returns
 */
const makeElement = (fObj) => {
    const element = fabricObjAttrsLookup[fObj.etype].reduce((p, c) => {
        p[c] = fObj[c];
        return p;
    }, {});
    if (fObj.etype === 'Path') {
        const path = element.path;
        element.path = [].concat
            .apply([], path)
            .map((e) => (typeof e === 'number' ? e.toFixed(1) : e))
            .join(' ');
    }
    return Object.assign({ etype: fObj.etype, id: fObj.id, transformMatrix: fObj.calcTransformMatrix() }, element);
};
/**
 * Make an extended fabric object, this is called when deserializing
 * the fabric object from data, or when creating a new shape from drawing event.
 * @param props
 * @returns
 */
const makeFabricObj = (props) => __awaiter(void 0, void 0, void 0, function* () {
    let newFObj;
    const fObjProps = Object.assign({}, props);
    if (fObjProps.transformMatrix) {
        delete fObjProps.transformMatrix;
        // keep the id and the etype
    }
    if (!fObjProps.etype || !fObjProps.id) {
        throw 'Can not make extended fabric object without etype and id.';
    }
    if (props.etype === 'Textbox') {
        newFObj = new fabric.Textbox('text', Object.assign(Object.assign({ text: 'Enter text...', fontFamily: 'Times New Roman' }, FabricObjectDefaults), fObjProps));
    }
    else if (props.etype === 'Path') {
        newFObj = new fabric.Path(props.path, Object.assign(Object.assign({ strokeLineCap: 'round' }, FabricObjectDefaults), fObjProps));
    }
    else if (props.etype === 'Rect') {
        newFObj = new fabric.Rect(Object.assign(Object.assign({}, FabricObjectDefaults), fObjProps));
    }
    else if (props.etype === 'Circle') {
        newFObj = new fabric.Circle(Object.assign(Object.assign({}, FabricObjectDefaults), fObjProps));
    }
    else if (props.etype === 'Ellipse') {
        newFObj = new fabric.Ellipse(Object.assign(Object.assign({}, FabricObjectDefaults), fObjProps));
    }
    else {
        throw Error(`Failed to deserialize element of unsupported type "${props.etype}".`);
    }
    // set the object position based on the transform matrix.
    if (props.transformMatrix) {
        const t = fabric.util.qrDecompose(props.transformMatrix);
        newFObj.set(t);
        newFObj.setPositionByOrigin({ x: t.translateX, y: t.translateY }, 'center', 'center');
    }
    newFObj.setCoords();
    return newFObj;
});
const getElementPropsFromUiState = (uiState) => {
    const { shape } = uiState;
    return {
        // ...uiState,
        // mode: CanvasMode;
        // showAnnotation: boolean;
        // shape: uiState.shape,
        fontSize: uiState.fontSize,
        color: uiState.color,
        fontFamily: uiState.fontFamily,
        etype: shape,
        fill: (['Textbox', 'SVG'].indexOf(shape) !== -1
            ? uiState.color
            : ''),
        stroke: (['Textbox', 'SVG'].indexOf(shape) !== -1
            ? ''
            : uiState.color),
        strokeWidth: (['Textbox'].indexOf(shape) !== -1
            ? 0
            : uiState.strokeWidth),
    };
};
const getUiStateFromElement = (element) => {
    const uiState = { shape: element.etype };
    if (['Textbox'].indexOf(element.etype) !== -1) {
        uiState.color = element.fill;
        uiState.fontSize = element.fontSize;
        uiState.fontFamily = element.fontFamily;
    }
    if (['Rect', 'Circle', 'Triangle', 'Ellipse', 'Path'].indexOf(element.etype) !=
        -1) {
        uiState.strokeWidth = element.strokeWidth;
        uiState.color = element.stroke;
    }
    return uiState;
};
const useCanvasDebugger = (on, elements, selection, fCanvasRef) => {
    // this is loggig for debugging only
    React.useEffect(() => {
        if (!on || !(fCanvasRef === null || fCanvasRef === void 0 ? void 0 : fCanvasRef.current))
            return;
        console.log('All elements: ', elements);
        if (fCanvasRef === null || fCanvasRef === void 0 ? void 0 : fCanvasRef.current) {
            console.log('All Fabric Objects: ', fCanvasRef.current.getObjects());
        }
        console.log('Selection Ids: ', selection);
        const elementSelection = elements.filter((e) => selection.indexOf(e.id) !== -1);
        if (elementSelection.length) {
            console.log('Selected: ', getUiStateFromElement(elementSelection[0]));
        }
    }, [elements, selection]);
};
const setHoverStyle = (newElement, selected, config) => {
    newElement.cornerStyle = 'circle';
    newElement.transparentCorners = false;
    newElement.cornerSize = 12;
    newElement.borderDashArray = selected ? undefined : [5, 5];
    newElement.borderColor = config === null || config === void 0 ? void 0 : config.selectionColor;
    newElement.cornerColor = config === null || config === void 0 ? void 0 : config.selectionColor;
};
/**
 * Set the selection handle for fabric shape and groups.
 * @param newElement new Fabric Object
 * @param elementType the fabric object type
 */
const setSelectionControls = (newElement, elementType, config) => {
    newElement.cornerStyle = 'circle';
    newElement.transparentCorners = false;
    newElement.cornerSize = 12;
    newElement.borderDashArray = undefined;
    newElement.borderColor = config === null || config === void 0 ? void 0 : config.selectionColor;
    newElement.cornerColor = config === null || config === void 0 ? void 0 : config.selectionColor;
    if (elementType === 'Textbox') {
        newElement.setControlVisible('tl', false);
        newElement.setControlVisible('mt', false);
        newElement.setControlVisible('tr', false);
        newElement.setControlVisible('bl', false);
        newElement.setControlVisible('mb', false);
        newElement.setControlVisible('br', false);
    }
};
const bootstrapHoverHandler = (newFObj) => {
    // set hover properties
    newFObj.on('mouseover', function () {
        this._renderControls(this.canvas.contextTop, {
            hasControls: false,
        });
    });
    newFObj.on('mousedown', function () {
        this.canvas.clearContext(this.canvas.contextTop);
    });
    newFObj.on('mouseout', function () {
        this.canvas.clearContext(this.canvas.contextTop);
    });
};
/**
 * find the sketch best matching the current time
 * @param mediaAnnotation
 * @param currentTime
 * @param hold
 * @param fps
 * @returns
 */
const findTimedSketch = (mediaAnnotation, currentTime, // in millisec
hold, fps) => {
    hold = hold === undefined ? false : hold;
    // find the matching key for the time.
    let matchSketch = null;
    let isKey = false;
    for (let i = 0; i < mediaAnnotation.length; i++) {
        const { time: keyTime } = mediaAnnotation[i];
        if (currentTime >= keyTime &&
            currentTime < keyTime + ANNOTATE_KEY_HOLD_MSEC) {
            matchSketch = mediaAnnotation[i];
            isKey = true;
        }
        if (hold && currentTime >= keyTime) {
            matchSketch = mediaAnnotation[i];
            if (currentTime < keyTime) {
                break;
            }
        }
    }
    return { timedSketch: matchSketch, isKey };
};
const findNextKeyTime = (keyList, currentTime) => {
    const adjKeys = keyList.filter((t) => currentTime < t);
    if (adjKeys.length === 0) {
        if (keyList.length === 0) {
            return null;
        }
        else {
            return keyList[0];
        }
    }
    else {
        return adjKeys[0];
    }
};
/**
 *
 * @param keyList
 * @param currentTime
 * @param fps
 * @param buffer some grace period for playback while current time advancing.
 * @returns
 */
const findPrevKeyTime = (keyList, currentTime, buffer) => {
    currentTime = getWholeMSecTime(currentTime) / 1000;
    currentTime = currentTime - (buffer || 0);
    const adjKeys = keyList.filter((t) => t < currentTime);
    if (adjKeys.length === 0) {
        if (keyList.length === 0) {
            return null;
        }
        else {
            return keyList[keyList.length - 1];
        }
    }
    else {
        return adjKeys[adjKeys.length - 1];
    }
};
const getWholeMSecTime = (time) => {
    return Math.floor(time * 1000);
};
const makeElementId = (etype) => {
    return `${etype}_${v4()}`;
};

function useCustomSelectCorners(fabricCanvasRef, selectionConfig) {
    React.useEffect(() => {
        fabricCanvasRef.current.on('selection:created', handle_selection);
        fabricCanvasRef.current.on('selection:updated', handle_selection);
        fabricCanvasRef.current.on('selection:cleared', handle_selection);
    }, []);
    // drag bound selection no longer create an event, hence can't query
    // selection that way, so hence use function getActiveObject.
    const handle_selection = () => {
        const activeObj = fabricCanvasRef.current.getActiveObject();
        if (activeObj) {
            setSelectionControls(activeObj, activeObj.etype, selectionConfig);
            // multiselections
            if (activeObj._objects) {
                const selection = activeObj._objects;
                selection.forEach((s) => setSelectionControls(s, s.etype, selectionConfig));
            }
        }
    };
}

// sync the hover style
// add the hover style to the selected
// remove the hover from unselected
function useCustomHoverStyle(fabricCanvasRef, selectionConfig) {
    React.useEffect(() => {
        if (selectionConfig === null || selectionConfig === void 0 ? void 0 : selectionConfig.hoverBoundingBox) {
            fabricCanvasRef.current.on('object:added', handle_object_added);
            fabricCanvasRef.current.on('path:created', handle_object_added);
            fabricCanvasRef.current.on('selection:created', handle_selection);
            fabricCanvasRef.current.on('selection:updated', handle_selection);
            fabricCanvasRef.current.on('selection:cleared', handle_selection);
        }
    }, []);
    const handle_object_added = (event) => {
        if (event.target) {
            setHoverStyle(event.target, false, selectionConfig);
            bootstrapHoverHandler(event.target);
        }
    };
    const handle_selection = (event) => {
        var _a, _b;
        (_a = event.selected) === null || _a === void 0 ? void 0 : _a.forEach((s) => {
            setHoverStyle(s, true, selectionConfig);
        });
        (_b = event.deselected) === null || _b === void 0 ? void 0 : _b.forEach((s) => {
            setHoverStyle(s, false, selectionConfig);
        });
    };
}

function useModifyHandler(fabricCanvasRef, onChangeElement) {
    React.useEffect(() => {
        fabricCanvasRef.current.on('object:modified', handle_object_modified);
    }, []);
    const handle_object_modified = (event) => {
        if (event.target) {
            let selection;
            if (event.target.hasOwnProperty('_objects')) {
                selection = event.target._objects;
            }
            else {
                selection = [event.target];
            }
            if (onChangeElement) {
                const changeBatch = [];
                selection.forEach((obj) => {
                    const updates = {
                        transformMatrix: obj.calcTransformMatrix(),
                    };
                    const fObj = obj;
                    if (fObj.etype === 'Textbox') {
                        updates.text = fObj.text;
                        updates.width = fObj.width;
                    }
                    changeBatch.push(Object.assign({ id: fObj.id }, updates));
                });
                onChangeElement(changeBatch);
            }
        }
    };
}

function useSyncSelection(fabricCanvasRef, uiState, setUIState, onSelection) {
    React.useEffect(() => {
        fabricCanvasRef.current.on('selection:created', (event) => {
            handle_selection_change(event.selected);
        });
        fabricCanvasRef.current.on('selection:updated', (event) => {
            handle_selection_change(event.selected, event.deselected);
        });
        fabricCanvasRef.current.on('selection:cleared', (event) => {
            handle_selection_change(event.selected, event.deselected);
        });
    }, []);
    const handle_selection_change = (added, removed) => {
        const activeObjs = fabricCanvasRef.current.getActiveObjects();
        const s = activeObjs.map((e) => e.id);
        added === null || added === void 0 ? void 0 : added.map((e) => e.id);
        removed === null || removed === void 0 ? void 0 : removed.map((e) => e.id);
        // sync the selected object state to the Ui controls
        // Note: the timeout is neccessary because if an object is preselected
        // then an new object gets selected, the new object sync the ui, and the ui
        // properties are applied to the pre-selection before selection is cleared.
        if (activeObjs.length === 1 && setUIState) {
            const obj = activeObjs[0];
            if (['Rect', 'Ellipse', 'Circle', 'Path'].includes(obj.etype)) {
                const updatedUiState = Object.assign(Object.assign({}, uiState), { strokeWidth: obj.strokeWidth, color: obj.stroke, shape: obj.etype });
                // The timeout
                setTimeout(setUIState, 200, updatedUiState);
            }
            if (['Textbox'].includes(obj.etype)) {
                setTimeout(setUIState, 200, Object.assign(Object.assign({}, uiState), { color: obj.fill, fontSize: obj.fontSize, shape: 'Textbox' }));
            }
        }
        if (onSelection) {
            onSelection(s);
        }
    };
}

/**
 * Depending on the type of element, the ui controls state affects the element attribute differently.
 * This hook examines the selected element and the ui state change, and instructs the apply handler.
 * @param uiState The state of the ui controls
 * @param selectedIds The current selected ids
 * @param elements all the annotation elements
 * @param applyUpdatesHandler
 */
function useApplyAttrsToSelection(canvasRef, fObjRegistryRef, uiState, selectedIds, elements, onChangeElement) {
    const updateSelectedElement = (key, value) => {
        const changeBatch = [];
        elements.forEach((element) => {
            if (selectedIds.indexOf(element.id) !== -1) {
                const etype = element.etype;
                let adjKey = key;
                if (etype === 'Textbox' && key === 'strokeWidth')
                    return;
                if (key === 'color') {
                    adjKey = etype === 'Textbox' ? 'fill' : 'stroke';
                }
                const fObj = fObjRegistryRef.current[element.id];
                if (fObj && fObj.get(adjKey) !== value) {
                    fObj.set(adjKey, value);
                    changeBatch.push({
                        id: element.id,
                        transformMatrix: fObj.calcTransformMatrix(),
                        [adjKey]: value,
                    });
                }
            }
            if (onChangeElement && changeBatch.length !== 0) {
                onChangeElement(changeBatch);
            }
        });
        canvasRef.current.renderAll();
    };
    useEffect(() => updateSelectedElement('fontSize', uiState.fontSize), [uiState.fontSize]);
    useEffect(() => updateSelectedElement('strokeWidth', uiState.strokeWidth), [uiState.strokeWidth]);
    useEffect(() => updateSelectedElement('color', uiState.color), [uiState.color]);
}

// Memo is used here as re-render can cause recreation of the expensive
// canvas element.
const FabricCanvas = React.memo((_a) => {
    var { width = 100, height = 100, backgroundColor = 'LightSteelBlue', attrs = DEFAULT_CANVAS_ATTRS } = _a, props = __rest(_a, ["width", "height", "backgroundColor", "attrs"]);
    const nativeCanvasRef = React.useRef(null);
    // canvas resize
    React.useEffect(() => {
        props.fabricCanvasRef.current.setWidth(width);
        props.fabricCanvasRef.current.setHeight(height);
    }, [width, height]);
    React.useEffect(() => {
        props.fabricCanvasRef.current.backgroundColor = backgroundColor;
    }, [backgroundColor]);
    /**
     * use effect
     */
    // mount object modify handle
    useEffect(() => {
        if (nativeCanvasRef.current !== null) {
            props.fabricCanvasRef.current = new fabric.Canvas(nativeCanvasRef.current.id, Object.assign({ width,
                height,
                backgroundColor }, attrs));
        }
        return () => {
            var _a;
            (_a = props.fabricCanvasRef.current) === null || _a === void 0 ? void 0 : _a.dispose();
        };
    }, []);
    return React.createElement("canvas", { ref: nativeCanvasRef, id: `canvas_${v4()}` });
});

const usePanZoom = (fabricCanvasRef, panZoom) => {
    React.useEffect(() => {
        if (panZoom) {
            fabricCanvasRef.current.setViewportTransform([
                panZoom.scale,
                0,
                0,
                panZoom.scale,
                panZoom.x,
                panZoom.y,
            ]);
        }
        fabricCanvasRef.current.clearContext(fabricCanvasRef.current.contextTop);
    }, [panZoom]);
};

function useRedrawElements(fabricCanvasRef, fObjRegistryRef, selection, backgroundColor, elements, redrawOnModify) {
    const redrawCanvas = (fCanvas) => __awaiter(this, void 0, void 0, function* () {
        fObjRegistryRef.current = {};
        // add the elements
        const fObjs = yield Promise.all(elements.map((ele) => makeFabricObj(ele)));
        elements.forEach((e, i) => {
            fObjRegistryRef.current[e.id] = fObjs[i]; // add to registry
        });
        fCanvas.clear();
        fCanvas.backgroundColor = backgroundColor;
        fabricCanvasRef.current.add(...fObjs);
        const selectedFObjs = [];
        elements.forEach((e, i) => {
            // build selection list
            if (selection.includes(e.id)) {
                selectedFObjs.push(fObjs[i]);
            }
        });
        if (selectedFObjs.length === 1) {
            // deal with the issue when you create a textbox, putting in the group
            // will enable showing all the resize corders.
            fCanvas.setActiveObject(selectedFObjs[0]);
        }
        else if (selectedFObjs.length !== 0) {
            const s = new fabric.ActiveSelection(selectedFObjs, {
                canvas: fCanvas,
            });
            fCanvas.setActiveObject(s);
        }
    });
    useEffect(() => {
        if (redrawOnModify) {
            redrawCanvas(fabricCanvasRef.current);
        }
    }, [elements]);
    useEffect(() => {
        if (!redrawOnModify) {
            redrawCanvas(fabricCanvasRef.current);
        }
    }, []);
}

const SHAPES = ['Ellipse', 'Rect', 'Circle', 'Triangle', 'Textbox'];
function useDrawShapeHandler(fabricCanvasRef, fObjRegistryRef, uiState, onAddElement, disabled) {
    const uiStateRef = React.useRef(uiState);
    const onAddElementFuncRef = React.useRef(onAddElement);
    useEffect(() => {
        onAddElementFuncRef.current = onAddElement;
    }, [onAddElement]);
    React.useEffect(() => {
        const fcanvas = fabricCanvasRef.current;
        fcanvas.on('mouse:down', draw_shape_handle_pointer_down);
        fcanvas.on('mouse:move', draw_shape_handle_pointer_move);
        fcanvas.on('mouse:up', draw_shape_handle_pointer_up);
        fcanvas.on('path:created', handle_path_created);
        fcanvas.defaultCursor = 'crosshair';
    }, []);
    React.useEffect(() => {
        uiStateRef.current = uiState;
        const fcanvas = fabricCanvasRef.current;
        if ((fcanvas.isDrawingMode =
            uiState.mode === 'draw' && uiState.shape === 'Path')) {
            syncBrush(fcanvas);
        }
    }, [uiState]);
    const syncBrush = (fcanvas) => {
        const brush = fcanvas.freeDrawingBrush;
        brush.width = uiState.strokeWidth || 1;
        brush.color = uiState.color || '#fff';
        fcanvas.freeDrawingBrush = brush;
    };
    const setPathDrawing = (fcanvas, uiState) => {
        // setting the free draw mode
        fcanvas.isDrawingMode = uiState.mode === 'draw' && uiState.shape === 'Path';
        syncBrush(fcanvas);
    };
    const handle_path_created = (meta) => {
        var _a;
        const fObj = meta.path;
        let k;
        for (k in FabricObjectDefaults) {
            fObj.set(k, FabricObjectDefaults[k]);
        }
        fObj.etype = 'Path';
        fObj.id = makeElementId('Path');
        (_a = onAddElementFuncRef.current) === null || _a === void 0 ? void 0 : _a.call(onAddElementFuncRef, makeElement(fObj));
        fObjRegistryRef.current[fObj.id] = fObj;
    };
    React.useEffect(() => {
        const fcanvas = fabricCanvasRef.current;
        if (uiState.mode) {
            setPathDrawing(fcanvas, uiState);
            const isDrawingMode = uiState.mode === 'draw';
            fcanvas.selection = !disabled && !isDrawingMode;
            fcanvas.defaultCursor = isDrawingMode ? 'crosshair' : '';
            fcanvas.skipTargetFind = isDrawingMode;
        }
    }, [uiState.mode, uiState.shape, uiState.shape, disabled]);
    const currentShapeDrawnRef = React.useRef(null);
    const draw_shape_handle_pointer_down = useCallback((opt) => {
        var _a, _b;
        const isDrawinShape = ((_a = uiStateRef.current) === null || _a === void 0 ? void 0 : _a.mode) === 'draw' &&
            SHAPES.indexOf((_b = uiStateRef.current) === null || _b === void 0 ? void 0 : _b.shape) !== -1;
        if (!isDrawinShape)
            return;
        const fcanvas = fabricCanvasRef.current;
        const pointer = fcanvas.getPointer(opt.e);
        const [originX, originY] = [pointer.x, pointer.y];
        const newElementDefaults = getElementPropsFromUiState(uiStateRef.current);
        // this is a preview element.
        makeFabricObj(Object.assign(Object.assign({}, newElementDefaults), { id: makeElementId(newElementDefaults.etype), transformMatrix: [1, 0, 0, 1, originX, originY] })).then((shape) => {
            fcanvas.add(shape);
            currentShapeDrawnRef.current = {
                originX,
                originY,
                newFObjExt: shape,
            };
            if (shape && shape.etype === 'Textbox') {
                fcanvas.setActiveObject(shape);
                shape.enterEditing();
                shape.selectAll();
                shape.selectionBackgroundColor = '#0006';
            }
        });
    }, []);
    const draw_shape_handle_pointer_move = useCallback((opt) => {
        var _a, _b, _c;
        const isDrawinShape = ((_a = uiStateRef.current) === null || _a === void 0 ? void 0 : _a.shape) !== 'Textbox' &&
            ((_b = uiStateRef.current) === null || _b === void 0 ? void 0 : _b.mode) === 'draw' &&
            SHAPES.indexOf((_c = uiStateRef.current) === null || _c === void 0 ? void 0 : _c.shape) !== -1;
        if (currentShapeDrawnRef.current === null || !isDrawinShape)
            return;
        const fcanvas = fabricCanvasRef.current;
        const pointer = fcanvas.getPointer(opt.e);
        const { originX, originY } = currentShapeDrawnRef.current;
        const fabObj = currentShapeDrawnRef.current.newFObjExt;
        if (SHAPES.indexOf(fabObj.etype) !== -1) {
            fabObj.set({
                left: Math.min(originX, pointer.x),
                top: Math.min(originY, pointer.y),
            });
        }
        else {
            fabObj.set({ left: pointer.x, top: pointer.y });
        }
        if (fabObj.etype === 'Ellipse') {
            fabObj.set({ rx: Math.abs(originX - pointer.x) / 2 });
            fabObj.set({ ry: Math.abs(originY - pointer.y) / 2 });
        }
        else if (fabObj.etype === 'Rect') {
            fabObj.set({ width: Math.abs(originX - pointer.x) });
            fabObj.set({ height: Math.abs(originY - pointer.y) });
        }
        fabObj.setCoords();
        fcanvas.requestRenderAll();
    }, []);
    const draw_shape_handle_pointer_up = useCallback((_opt) => {
        var _a, _b;
        const isDrawinShape = 
        // uiStateRef.current?.mode === 'draw' &&
        SHAPES.indexOf((_a = uiStateRef.current) === null || _a === void 0 ? void 0 : _a.shape) !== -1;
        const fcanvas = fabricCanvasRef.current;
        if (currentShapeDrawnRef.current === null || !isDrawinShape)
            return;
        const { newFObjExt } = currentShapeDrawnRef.current;
        if (SHAPES.indexOf(newFObjExt.etype) !== -1 &&
            (newFObjExt.width < 4 || newFObjExt.height < 4)) {
            // don't add it if it's too small
            fcanvas === null || fcanvas === void 0 ? void 0 : fcanvas.remove(newFObjExt);
        }
        else {
            (_b = onAddElementFuncRef.current) === null || _b === void 0 ? void 0 : _b.call(onAddElementFuncRef, makeElement(newFObjExt));
            fObjRegistryRef.current[newFObjExt.id] = newFObjExt;
        }
        currentShapeDrawnRef.current = null;
    }, []);
}

React.createContext({
    uiState: {},
});
// this is use for initalization purpose.
// this is dangerous to be used within a component as creates an expensive
// canvas dom, and if not manage property, re-renders can quickly exhause memory.
// Hence have it outside makes more sense.
const EmptyCanvas = new fabric.Canvas('');
const AnnotateCanvas$1 = React.memo((_a) => {
    var { elements = [], selection = undefined, onSelection = undefined, width = 100, height = 100, backgroundColor = '', clearOnElementModify = false, uiState = DEFAULT_UI_ATTRS, setUiState, selectionConfig = DEFAULT_SELECTION_CONFIG, debugLogging = false, disabled = false } = _a, props = __rest(_a, ["elements", "selection", "onSelection", "width", "height", "backgroundColor", "clearOnElementModify", "uiState", "setUiState", "selectionConfig", "debugLogging", "disabled"]);
    const fcRef = React.useRef(EmptyCanvas);
    const fObjRegistryRef = React.useRef({});
    const [innerSelection, setInnerSelection] = useState([]);
    if (!selection)
        selection = innerSelection;
    if (!onSelection)
        onSelection = setInnerSelection;
    useRedrawElements(fcRef, fObjRegistryRef, selection, backgroundColor, elements, clearOnElementModify);
    useSyncSelection(fcRef, uiState, setUiState, onSelection);
    useCustomSelectCorners(fcRef, selectionConfig);
    useCustomHoverStyle(fcRef, selectionConfig);
    useDrawShapeHandler(fcRef, fObjRegistryRef, uiState, props.onAddElement, disabled);
    useModifyHandler(fcRef, props.onChangeElement);
    usePanZoom(fcRef, props.panZoom);
    useApplyAttrsToSelection(fcRef, fObjRegistryRef, uiState, selection, elements, props.onChangeElement);
    useCanvasDebugger(debugLogging, elements, selection, fcRef);
    return (React.createElement("div", { style: { pointerEvents: disabled ? 'none' : undefined } },
        React.createElement(FabricCanvas, { fabricCanvasRef: fcRef, width: width, height: height, backgroundColor: backgroundColor })));
});

function addNewKey(timedSketches, currentTime, elements = []) {
    const updated = [...timedSketches];
    const key = getWholeMSecTime(currentTime);
    const newSketch = {
        id: `sketch_${v4()}`,
        time: key,
        sketch: elements,
    };
    updated.push(newSketch);
    updated.sort((a, b) => a.time - b.time);
    return { newSketch, updated };
}
function timedSketchActionReducer(state, action) {
    var _a;
    const currentSketch = ((_a = state.currentTimedSketch) === null || _a === void 0 ? void 0 : _a.sketch) || [];
    const { timedSketches, currentTimedSketch, currentTime } = state;
    const addElements = (newElements, autoSelect = true, forceRedraw = false) => {
        const newElementIds = newElements.map((e) => e.id);
        if (state.isKey) {
            if (forceRedraw) {
                currentTimedSketch.sketch = [
                    ...currentTimedSketch.sketch,
                    ...newElements,
                ];
            }
            else {
                currentTimedSketch.sketch.push(...newElements);
            }
            return Object.assign(Object.assign({}, state), { selection: autoSelect ? newElementIds : [] });
        }
        else {
            const newKey = addNewKey(timedSketches, currentTime, newElements);
            return Object.assign(Object.assign({}, state), { timedSketches: newKey.updated, isKey: true, keyTime: newKey.newSketch.time });
        }
    };
    switch (action.type) {
        case 'reload':
            return Object.assign(Object.assign({}, state), { timedSketches: action.timedSketches });
        case 'addElement':
            if (!action.newElement.id) {
                throw Error(`Unexpected add element with no new id: ${action.newElement}`);
            }
            return addElements([action.newElement], action.newElement.etype !== 'Path', false);
        case 'removeElements':
            currentTimedSketch.sketch = currentSketch.filter((x) => !action.ids.includes(x.id));
            return Object.assign(Object.assign({}, state), { timedSketches: [...timedSketches] });
        case 'selectElements':
            return Object.assign(Object.assign({}, state), { selection: action.selection });
        case 'copySelected':
            const elms = currentSketch.filter((e) => state.selection.includes(e.id));
            window.localStorage.setItem('clipboard_elements', JSON.stringify(elms));
            return state;
        case 'paste':
            const elmsStr = window.localStorage.getItem('clipboard_elements');
            if (elmsStr) {
                const elms = JSON.parse(elmsStr);
                elms.forEach((e) => {
                    e.id = makeElementId(e.etype);
                });
                return addElements(elms, true, true);
            }
            return state;
        case 'changeElement':
            const elementIndexById = currentSketch.reduce((a, v, i) => {
                a[v.id] = i;
                return a;
            }, {});
            action.elementUpdates.forEach((updates) => {
                const index = elementIndexById[updates.id];
                Object.keys(updates).forEach((k) => (currentSketch[index][k] = updates[k]));
            });
            return state;
        case 'updateCurrentTime':
            const { timedSketch, isKey } = findTimedSketch(state.timedSketches, getWholeMSecTime(action.time), false);
            const keyTime = isKey ? timedSketch.time : null;
            return Object.assign(Object.assign({}, state), { currentTimedSketch: timedSketch, currentTime: action.time, keyTime,
                isKey });
        case 'addKey':
            const addNewKeyResult = addNewKey(timedSketches, currentTime);
            return Object.assign(Object.assign({}, state), { timedSketches: addNewKeyResult.updated, isKey: true, keyTime: addNewKeyResult.newSketch.time });
        case 'removeKey':
            return Object.assign(Object.assign({}, state), { timedSketches: state.timedSketches.filter((x) => { var _a; return x.id !== ((_a = state.currentTimedSketch) === null || _a === void 0 ? void 0 : _a.id); }) });
        default:
            return state;
    }
}

const PlayerContext = React.createContext({
    mediaList: [],
    mediaIndex: 0,
    setMediaIndex: () => { },
    playing: false,
    setPlaying: () => { },
    muted: false,
    setMuted: () => { },
    fullScreen: false,
    setFullScreen: () => { },
    seeking: false,
    setSeeking: () => { },
    seekTime: undefined,
    setSeekTime: () => { },
    seekTimeReady: false,
});
const PlaybackContext = React.createContext({
    playerState: { played: 0, loaded: 0, duration: 0 },
    setPlayerState: () => { },
});
const PlayerContextProvider = (props) => {
    const [seekTime, setSeekTime] = useState(); // seek time
    const [playing, setPlaying] = useState(false);
    const [muted, setMuted] = useState(false);
    const [playerState, setPlayerState] = useState({
        played: 0,
        loaded: 0,
        duration: 0,
    });
    useEffect(() => {
        // clear the seek time, this is ensure that user can repeatly seek the
        // same location multiple times.
        const to = window.setTimeout(setSeekTime, 500, undefined);
        return () => window.clearTimeout(to);
    }, [seekTime]);
    const seekTimeReady = !!playerState.loaded || (!!playerState.played && playerState.played !== 0);
    return (React.createElement(PlayerContext.Provider, { value: Object.assign(Object.assign({}, props), { seekTimeReady,
            playing,
            setPlaying,
            muted,
            setMuted,
            seekTime,
            setSeekTime }) },
        React.createElement(PlaybackContext.Provider, { value: {
                playerState,
                setPlayerState,
            } }, props.children)));
};

const formatTimeDisplay = (time, duration) => {
    const mSec = new Date(time * 1000);
    mSec.toISOString();
    if (duration > 60) {
        const result = [];
        const hour = mSec.getHours() - 19;
        const min = mSec.getMinutes();
        const sec = (mSec.getSeconds() + mSec.getMilliseconds() / 1000).toFixed(0).padStart(2, '0');
        if (hour) {
            result.push(hour);
        }
        result.push(hour ? min.toString().padStart(2, '0') : min);
        result.push(sec);
        return result.join(':');
        // } else if (duration > 60) {
        //   return `${mSec.getMinutes()}:${(
        //     mSec.getMilliseconds() / 1000 +
        //     mSec.getSeconds()
        //   )
        //     .toFixed(0)
        //     .padStart(2, '0')}`;
    }
    else {
        return time.toFixed(1);
    }
};

const MediaAnnotateContext = React.createContext({
    annotateTimeList: [],
    elements: [],
    onAddElement: () => { },
    onChangeElement: () => { },
    onRemoveElements: () => { },
    isKey: false,
    keyTime: null,
    onAddKey: () => { },
    onRemoveKey: () => { },
    selection: [],
    onSelection: () => { },
});
const MediaAnnotationContextProvider = ({ mediaAnnotation, setMediaAnnotation, children }) => {
    var _a;
    const { playerState } = useContext(PlaybackContext);
    const [annotateTimeList, setAnnotateTimeList] = useState([]);
    const [mediaAnnotationState, mediaAnnotationDispatch] = useReducer(timedSketchActionReducer, {
        timedSketches: mediaAnnotation,
        currentTimedSketch: null,
        currentTime: -1,
        isKey: false,
        keyTime: null,
        selection: [],
    });
    useEffect(() => {
        mediaAnnotationDispatch({
            type: 'reload',
            timedSketches: mediaAnnotation,
        });
    }, [mediaAnnotation]);
    useEffect(() => {
        // push the changes up
        setMediaAnnotation(mediaAnnotationState.timedSketches);
        // now refresh the annotations.
        mediaAnnotationDispatch({
            type: 'updateCurrentTime',
            time: playerState.played,
        });
    }, [mediaAnnotationState.timedSketches]);
    useEffect(() => {
        const markers = mediaAnnotationState.timedSketches
            // convert millisec back to sec
            .map((m) => m.time / 1000)
            // prevent faulty markers
            .filter((m) => m <= playerState.duration && m >= 0);
        setAnnotateTimeList(markers);
    }, [mediaAnnotationState.timedSketches, playerState.duration]);
    useEffect(() => {
        mediaAnnotationDispatch({
            type: 'updateCurrentTime',
            time: playerState.played,
        });
    }, [playerState.played]);
    const onAddElement = useCallback((newElement) => mediaAnnotationDispatch({ type: 'addElement', newElement }), []);
    const onChangeElement = useCallback((elementUpdates) => mediaAnnotationDispatch({ type: 'changeElement', elementUpdates }), []);
    const onAddKey = useCallback(() => {
        mediaAnnotationDispatch({ type: 'addKey' });
    }, []);
    const onRemoveKey = useCallback(() => {
        if (confirm(`Remove annotations at time: ${formatTimeDisplay(playerState.played, playerState.duration)}?`)) {
            mediaAnnotationDispatch({ type: 'removeKey' });
        }
    }, [playerState]);
    const onSelection = useCallback((selection) => mediaAnnotationDispatch({ type: 'selectElements', selection }), []);
    const onRemoveElements = useCallback((ids) => mediaAnnotationDispatch({ type: 'removeElements', ids }), []);
    const onCopySelected = useCallback(() => mediaAnnotationDispatch({ type: 'copySelected' }), []);
    const onPaste = useCallback(() => mediaAnnotationDispatch({ type: 'paste' }), []);
    const elements = ((_a = mediaAnnotationState.currentTimedSketch) === null || _a === void 0 ? void 0 : _a.sketch) || [];
    return (React.createElement(MediaAnnotateContext.Provider, { value: {
            annotateTimeList,
            elements,
            onAddElement,
            onChangeElement,
            onRemoveElements,
            isKey: mediaAnnotationState.isKey,
            keyTime: mediaAnnotationState.keyTime,
            onAddKey,
            onRemoveKey,
            selection: mediaAnnotationState.selection,
            onSelection,
            onCopySelected,
            onPaste,
        } }, children));
};

/**
 * Returns the pan zoom specs for center fit content
 * @param content the dimensions of the content
 * @param container the dimensions of the container
 */
const getContentFitSpec = (content, container) => {
    const viewerAspect = container.width / container.height;
    const contentAspect = content.width / content.height;
    const ratioWidth = container.width / content.width;
    const ratioHeight = container.height / content.height;
    const ratio = viewerAspect > contentAspect ? ratioHeight : ratioWidth;
    const scale = ratio;
    const x = Math.abs(container.width - content.width * scale) / 2;
    const y = Math.abs(container.height - content.height * scale) / 2;
    return { x, y, scale };
};
/**
 * Coordinates from touches, mouse clicks are relative to the screen,
 * this updates them to be relative to the target element.
 */
const getAbsoluteCoords = (coords, boundingRect) => {
    return {
        x: coords.clientX - boundingRect.left,
        y: coords.clientY - boundingRect.top,
    };
};
const getDistance = (points) => {
    if (points.length >= 2) {
        return Math.sqrt(Math.pow(points[0].x - points[1].x, 2) +
            Math.pow(points[0].y - points[1].y, 2));
    }
    return 0;
};
const getMidPoint = (points) => {
    if (points.length === 2) {
        return {
            x: (points[0].x + points[1].x) / 2,
            y: (points[0].y + points[1].y) / 2,
        };
    }
    return points[0];
};
/**
 * Coordinates from touches are relative to the screen,
 * this updates them to be relative to the target element.
 */
const getTouchCoords = (evt) => {
    return Array.from(evt.touches).map((t) => getAbsoluteCoords(t, evt.currentTarget.getBoundingClientRect()));
};
/**
 * Normalize the resolution of annotate canvas for the content so that
 * the canvas resolution stays same regardless of the content resolution.
 * @param contentSize content width and height in pixel
 * @param scale the content pixel to screen pixel scale
 * @param resolution the desired annotate canvas resolution
 * @returns the normalized scale
 */
const normalizeScale = (contentSize, scale, resolution) => {
    return (Math.max(contentSize.width / resolution, contentSize.height / resolution) *
        scale);
};
const normalizeSize = (contentSize, resolution) => {
    const widthRatio = contentSize.width / resolution;
    const heightRatio = contentSize.height / resolution;
    if (widthRatio > heightRatio) {
        return {
            width: resolution,
            height: (contentSize.height / contentSize.width) * resolution,
        };
    }
    else {
        return {
            height: resolution,
            width: (contentSize.width / contentSize.height) * resolution,
        };
    }
};

/**
 * This hook monitors an dom element's dimension
 * @param ref reference to the html element
 * @returns the update container size.
 */
const useMonitorResizable = (ref) => {
    const [containerSize, setContainerSize] = React.useState({
        width: 10,
        height: 10,
    });
    // add obsever to the content viewer container
    React.useEffect(() => {
        const ro = new ResizeObserver((entries) => {
            if (entries.length > 0) {
                setContainerSize({
                    width: entries[0].contentRect.width,
                    height: entries[0].contentRect.height,
                });
            }
        });
        const element = ref.current;
        if (element) {
            ro.observe(element);
        }
        return () => ro.disconnect();
    }, [ref]);
    return containerSize;
};
/**
 *
 * @param posOri the offset from the image top left corner to the container
 * @param pointerPosOri start pointer position relative to container
 * @param pointerPosNow current pointer position relative to container
 * @param scaleOri original scale factor
 * @param scaleNow current scale factor
 */
const getNewPosition = (posOri, pointerPosOri, scaleOri, pointerPosNow, scaleNow) => {
    // the pointer position in object space,
    // the relative center for zoom/pinch.
    const objPos = (pointerPosOri - posOri) / scaleOri;
    return pointerPosNow - objPos * scaleNow;
};
const useTouchPanZoom = (panZoomState, setPanZoomState, contentFitSpec, disabled) => {
    const touchState = useRef();
    const [inProgress, setInProgress] = useState(false);
    const handleTouchMove = (evt) => {
        if (!touchState.current)
            return;
        setInProgress(true);
        const oriPanZoom = touchState.current.oriPanZoom;
        const touchesStart = touchState.current.start;
        const touchesNow = getTouchCoords(evt);
        const startMid = getMidPoint([touchesStart[0], touchesStart[1]]);
        const nowMid = getMidPoint(touchesNow);
        let scale = panZoomState.scale;
        if (evt.touches.length === 2) {
            const distancePrev = getDistance([touchesStart[0], touchesStart[1]]);
            const distanceNow = getDistance(touchesNow);
            scale = (distanceNow / distancePrev) * oriPanZoom.scale;
            //  bound it.
            scale = Math.min(contentFitSpec.current.max * contentFitSpec.current.fitSpec.scale, Math.max(contentFitSpec.current.min * contentFitSpec.current.fitSpec.scale, scale));
        }
        const x = getNewPosition(oriPanZoom.x, startMid.x, oriPanZoom.scale, nowMid.x, scale);
        const y = getNewPosition(oriPanZoom.y, startMid.y, oriPanZoom.scale, nowMid.y, scale);
        setPanZoomState({ x, y, scale });
    };
    return {
        onTouchStart: (evt) => {
            if (disabled)
                return;
            touchState.current = {
                start: getTouchCoords(evt),
                oriPanZoom: Object.assign({}, panZoomState),
            };
        },
        onTouchEnd: (evt) => {
            if (evt.touches.length === 0) {
                touchState.current = undefined;
                setInProgress(false);
            }
        },
        onTouchMove: handleTouchMove,
        inProgress,
    };
};
const useWheelZoom = (panZoomState, setPanZoomState, contentFitSpec, disabled) => {
    const handleWheel = (evt) => {
        if (disabled)
            return;
        const coords = getAbsoluteCoords(evt, evt.currentTarget.getBoundingClientRect());
        const deltaY = evt.deltaY;
        const scaleDelta = deltaY * -0.002;
        const scaleTarget = panZoomState.scale + scaleDelta;
        //  bound it.
        const scale = Math.min(contentFitSpec.current.max * contentFitSpec.current.fitSpec.scale, Math.max(contentFitSpec.current.min * contentFitSpec.current.fitSpec.scale, scaleTarget));
        const x = getNewPosition(panZoomState.x, coords.x, panZoomState.scale, coords.x, scale);
        const y = getNewPosition(panZoomState.y, coords.y, panZoomState.scale, coords.y, scale);
        if (evt.deltaX === 0) {
            setPanZoomState({ x, y, scale });
        }
    };
    return {
        onWheel: handleWheel,
    };
};
const usePointerPan = (panZoomState, setPanZoomState, disabled) => {
    const pointerState = useRef();
    const [inProgress, setInProgress] = useState(false);
    return {
        onPointerDown: (evt) => {
            if ((disabled && evt.button === 0) || evt.button === 2)
                return;
            const coords = getAbsoluteCoords(evt, evt.currentTarget.getBoundingClientRect());
            setInProgress(true);
            pointerState.current = { start: coords, oriPanZoom: Object.assign({}, panZoomState) };
        },
        onPointerUp: () => {
            pointerState.current = undefined;
            setInProgress(false);
        },
        onPointerMove: (evt) => {
            if ((disabled && evt.button === 0) || evt.button === 2)
                return;
            const coords = getAbsoluteCoords(evt, evt.currentTarget.getBoundingClientRect());
            if (pointerState.current) {
                const x = getNewPosition(pointerState.current.oriPanZoom.x, pointerState.current.start.x, pointerState.current.oriPanZoom.scale, coords.x, panZoomState.scale);
                const y = getNewPosition(pointerState.current.oriPanZoom.y, pointerState.current.start.y, pointerState.current.oriPanZoom.scale, coords.y, panZoomState.scale);
                setPanZoomState({ x, y, scale: panZoomState.scale });
            }
        },
        inProgress,
    };
};

const dummyConRef = React.createRef();
const dummyFitRef = {
    current: {
        fitSpec: { x: 0, y: 0, scale: 1.0 },
        min: 1,
        max: 1,
    },
};
const PanZoomContext = React.createContext({
    contentSize: { width: 10, height: 10 },
    setContentSize: () => { },
    containerSize: { width: 10, height: 10 },
    panZoom: { x: 0, y: 0, scale: 1.0 },
    setPanZoom: () => { },
    topContainerRef: dummyConRef,
    contentFitSpecRef: dummyFitRef,
});
const PanZoomContextProvider = (props) => {
    const [panZoomState, setPanZoomState] = useState({ x: 0, y: 0, scale: 1.0 });
    const topContainerRef = useRef(null);
    const contentFitSpecRef = useRef({
        fitSpec: { x: 0, y: 0, scale: 1.0 },
        min: 1,
        max: 1,
    });
    // if either the container or content resize, center fit the content;
    const containerSize = useMonitorResizable(topContainerRef);
    const [contentSize, setContentSize] = useState({ width: 10, height: 10 });
    return (React.createElement(PanZoomContext.Provider, { value: {
            panZoom: panZoomState,
            setPanZoom: setPanZoomState,
            containerSize,
            contentSize,
            setContentSize,
            topContainerRef,
            contentFitSpecRef,
        } }, props.children));
};

/**
 * PinchZoomPan gesture on a web page manipuate the entire website,
 * This prevents that for the reference element.
 * @param ref html dom element where default guesture will be disabled.
 */
const usePreventDefaultBrowserTouch = (ref, event) => {
    useEffect(() => {
        var _a;
        (_a = ref.current) === null || _a === void 0 ? void 0 : _a.addEventListener(event || 'touchstart', (evt) => {
            evt.preventDefault();
        }, false);
    }, []);
};
/**
 * Trackpad/mouse wheel gesture on a web page manipuate the entire website,
 * This prevents that for the reference element.
 * @param ref html dom element where default guesture will be disabled.
 */
const usePreventDefaultBrowserWheel = (ref) => {
    useEffect(() => {
        var _a;
        (_a = ref.current) === null || _a === void 0 ? void 0 : _a.addEventListener('wheel', (evt) => {
            evt.preventDefault();
        }, false);
    }, []);
};
/**
 * Prevent dragging on a web dom element
 * @param ref html dom element where default guesture will be disabled.
 */
const usePreventDefaultBrowserPointer = (ref, disabled) => {
    useEffect(() => {
        var _a;
        (_a = ref.current) === null || _a === void 0 ? void 0 : _a.addEventListener('pointerdown', (evt) => {
            evt.preventDefault();
        }, false);
    }, [disabled]);
};
const isIOS = () => {
    return (!!navigator.maxTouchPoints &&
        navigator.maxTouchPoints > 2 &&
        /Mac/.test(navigator.userAgent));
};
const userAgent = navigator.userAgent.toLowerCase();
const isTablet = () => {
    const queryAgent = /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(userAgent);
    return queryAgent || isIOS();
};
const useFullScreen = () => {
    const element = useRef(null);
    const [fullScreen, setFullScreen] = useState(false);
    const syncFullScreen = () => {
        setFullScreen(!!(document === null || document === void 0 ? void 0 : document.fullscreenElement));
    };
    useEffect(() => {
        var _a;
        (_a = element.current) === null || _a === void 0 ? void 0 : _a.addEventListener('fullscreenchange', syncFullScreen);
        return () => { var _a; return (_a = element.current) === null || _a === void 0 ? void 0 : _a.removeEventListener('fullscreenchange', syncFullScreen); };
    }, []);
    const toggleFullScreen = () => {
        var _a;
        if (!!(document === null || document === void 0 ? void 0 : document.fullscreenElement)) {
            document.exitFullscreen();
            syncFullScreen();
        }
        else {
            (_a = element.current) === null || _a === void 0 ? void 0 : _a.requestFullscreen().then(syncFullScreen).catch((err) => {
                alert(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
            });
        }
    };
    return {
        fullScreenElement: element,
        fullScreen,
        setFullScreen: toggleFullScreen,
    };
};

var browser = /*#__PURE__*/Object.freeze({
    __proto__: null,
    usePreventDefaultBrowserTouch: usePreventDefaultBrowserTouch,
    usePreventDefaultBrowserWheel: usePreventDefaultBrowserWheel,
    usePreventDefaultBrowserPointer: usePreventDefaultBrowserPointer,
    isTablet: isTablet,
    useFullScreen: useFullScreen
});

const PanZoomOverlay = (props) => {
    const ctx = useContext(PanZoomContext);
    return (React.createElement("div", { className: "panzoom_overlay", style: {
            pointerEvents: props.pointerEventPassthrough ? 'none' : undefined,
        } }, props.render
        ? props.render(ctx.panZoom, ctx.contentSize, ctx.containerSize)
        : props.children));
};
const PanZoomContent = (props) => {
    const ctx = useContext(PanZoomContext);
    return (React.createElement("div", { style: Object.assign({ transformOrigin: 'left top', position: 'relative', userSelect: 'none', transition: 'transform 0.05s', transform: `translate(${ctx.panZoom.x}px, ${ctx.panZoom.y}px) scale(${ctx.panZoom.scale})` }, ctx.contentSize) }, props.render ? props.render(ctx.setContentSize) : props.children));
};
const PanZoomWithContext = (props) => {
    return (React.createElement(PanZoomContextProvider, null,
        React.createElement(PanZoomContainer, Object.assign({}, props),
            React.createElement("div", null, props.children))));
};
const PanZoomContainer = (_a) => {
    var { disabled = false, zoomBounds = { min: 0.5, max: 5 } } = _a, props = __rest(_a, ["disabled", "zoomBounds"]);
    const { topContainerRef, containerSize, contentSize, panZoom: panZoomState, setPanZoom: setPanZoomState, contentFitSpecRef, } = useContext(PanZoomContext);
    usePreventDefaultBrowserTouch(topContainerRef);
    usePreventDefaultBrowserWheel(topContainerRef);
    useLayoutEffect(() => {
        const fitSpec = getContentFitSpec(contentSize, containerSize);
        contentFitSpecRef.current = Object.assign({ fitSpec }, zoomBounds);
        setPanZoomState(fitSpec);
    }, [contentSize, containerSize]);
    const _b = useTouchPanZoom(panZoomState, setPanZoomState, contentFitSpecRef, disabled), { inProgress: touchCaptured } = _b, touchHandlers = __rest(_b, ["inProgress"]);
    const wheelHandler = useWheelZoom(panZoomState, setPanZoomState, contentFitSpecRef);
    const _c = usePointerPan(panZoomState, setPanZoomState, disabled || touchCaptured), pointerHandler = __rest(_c, ["inProgress"]);
    const flgTablet = isTablet();
    const transformWrapper = (React.createElement("div", Object.assign({ ref: topContainerRef, className: "panzoom_top_container" }, (flgTablet ? touchHandlers : {}), (flgTablet ? {} : wheelHandler), (flgTablet ? {} : pointerHandler)), props.children));
    return transformWrapper;
};

const PlayDeck$1 = (_a) => {
    var { disablePlay = false, disableMute = false, disablePrev = false, disableNext = false, disableNextAnnotation = false, disablePrevAnnotation = false, themeColor = 'white', iconSize = undefined } = _a, props = __rest(_a, ["disablePlay", "disableMute", "disablePrev", "disableNext", "disableNextAnnotation", "disablePrevAnnotation", "themeColor", "iconSize"]);
    return (React.createElement("div", { className: 'playToolbar' },
        props.onSkipPrev && (React.createElement(IconButton, { size: iconSize, onClick: props.onSkipPrev, disabled: disablePrev, style: { color: themeColor, opacity: disablePrev ? 0.2 : 1 }, title: 'Skip prev' },
            React.createElement(SkipPreviousIcon, { fontSize: iconSize }))),
        React.createElement(IconButton, { size: iconSize, onClick: props.onPlay, disabled: disablePlay, style: { color: themeColor, opacity: disablePlay ? 0.2 : 1 }, title: 'Play' }, props.playing ? (React.createElement(PauseIcon, { fontSize: iconSize })) : (React.createElement(PlayArrowIcon, { fontSize: iconSize }))),
        props.onSkipNext && (React.createElement(IconButton, { size: iconSize, onClick: props.onSkipNext, disabled: disableNext, style: { color: themeColor, opacity: disableNext ? 0.2 : 1 }, title: 'Skip next' },
            React.createElement(SkipNextIcon, { fontSize: iconSize }))),
        props.onToggleLoop && (React.createElement(IconButton, { size: "small", onClick: props.onToggleLoop, title: props.loopType },
            React.createElement(Badge, { badgeContent: props.loopType === 'loopCurrent' ? 1 : null, style: { color: props.loopType === 'off' ? undefined : themeColor } },
                React.createElement(LoopIcon, { style: {
                        fill: props.loopType === 'off' ? undefined : themeColor,
                    } })))),
        props.onNextAnnotation !== undefined && (React.createElement(React.Fragment, null,
            React.createElement(IconButton, { size: iconSize, onClick: props.onPrevAnnotation, style: {
                    color: themeColor,
                    opacity: disablePrevAnnotation ? 0.2 : 1,
                }, disabled: disablePrevAnnotation, title: 'Prev annotation' },
                React.createElement(ChevronLeftIcon, { fontSize: iconSize })),
            React.createElement(IconButton, { size: iconSize, onClick: props.onNextAnnotation, style: {
                    color: themeColor,
                    opacity: disableNextAnnotation ? 0.2 : 1,
                }, disabled: disableNextAnnotation, title: 'Next annotation' },
                React.createElement(ChevronRightIcon, { fontSize: iconSize })))),
        React.createElement("div", { style: { flexGrow: 1 } }),
        props.onMuted && (React.createElement(IconButton, { disabled: disableMute, size: iconSize, onClick: props.onMuted, title: "Toggle Mute", style: {
                color: props.muted ? 'red' : themeColor,
                opacity: disableMute ? 0.2 : 1,
            } }, props.muted ? (React.createElement(VolumeOffIcon, { fontSize: iconSize })) : (React.createElement(VolumeUpIcon, { fontSize: iconSize })))),
        props.onFullScreen && (React.createElement(IconButton, { size: iconSize, onClick: props.onFullScreen, title: "Toggle Fullscreen" }, props.fullScreen ? (React.createElement(FullscreenExitIcon, { fontSize: iconSize, style: { color: themeColor } })) : (React.createElement(FullscreenIcon, { fontSize: iconSize, style: { color: themeColor } })))),
        React.createElement("div", { style: { width: '10px' } })));
};

// the millesec of delay between emitting scrubbing updates.
const SCRUB_UPDATE_DELAY = 5;
const Timeline$1 = (_a) => {
    var { frameRange, useFrameDisplay = false, themeColor = 'red' } = _a, props = __rest(_a, ["frameRange", "useFrameDisplay", "themeColor"]);
    const progressDisplayTimeoutRef = useRef(null);
    const [seekPercentage, setSeekPercentage] = useState(0);
    const [innerCaptured, setInnerCaptured] = useState(false);
    const timelineCaptured = props.timelineCaptured || innerCaptured;
    const onTimelineCaptured = props.onTimelineCaptured || setInnerCaptured;
    const playPercentage = props.currentTime / props.duration; // actual percentage
    // display perentage as adjust at frame discret increment steps.
    let displayPlayPercentage = playPercentage;
    let oneFramePercent = 0;
    let frameDuration = -1;
    let frameIndex = -1;
    if (timelineCaptured) {
        displayPlayPercentage = seekPercentage;
    }
    if (useFrameDisplay && frameRange) {
        frameDuration = frameRange[1] - frameRange[0] + 1;
        oneFramePercent = 1 / frameDuration;
        // this is due movie showing first frame at 0%)
        displayPlayPercentage = Math.min(displayPlayPercentage, 1 - oneFramePercent);
        const frameElapsed = Math.floor(displayPlayPercentage * frameDuration);
        frameIndex = frameElapsed + frameRange[0];
        displayPlayPercentage = Math.min(frameElapsed * oneFramePercent);
    }
    React.useEffect(() => {
        return () => {
            if (progressDisplayTimeoutRef.current !== null) {
                window.clearTimeout(progressDisplayTimeoutRef.current);
            }
        };
    }, []);
    const handleScrubStart = (e) => {
        const isPointerDownEvent = e.type && e.type === 'pointerdown';
        // have the canvas capture the mouse events until it's released
        if (isPointerDownEvent) {
            e.currentTarget.setPointerCapture(e.pointerId);
        }
        if (onTimelineCaptured)
            onTimelineCaptured(true);
        fireSeekEvent(e);
    };
    /**
     * User as moved the playhead either by
     * 1) selected the thumb and moving it
     * 2) click anywhere on the timeline.
     */
    const handleScrubMove = (e) => {
        if (!timelineCaptured)
            return;
        fireSeekEvent(e);
    };
    const handleScrubEnd = (e) => {
        const isPointerEvent = e.type && e.type.startsWith('pointer');
        // have the canvas capture the mouse events until it's released
        if (isPointerEvent) {
            e.currentTarget.releasePointerCapture(e.pointerId);
        }
        if (onTimelineCaptured)
            onTimelineCaptured(false);
    };
    const fireSeekEvent = (e) => {
        const isPointerEvent = e.type && e.type.startsWith('pointer');
        const isPointerMoveEvent = e.type && e.type === 'pointermove';
        if (isPointerMoveEvent) {
            if (props.onPointerMove) {
                props.onPointerMove(e);
            }
            if (e.buttons === 0) {
                // no button is pressed, return
                return;
            }
        }
        e.stopPropagation();
        const bounds = e.target.getBoundingClientRect();
        if (bounds.width === 0) {
            console.warn('bounds for timeline is 0');
            return;
        }
        const totalWidth = bounds.width;
        let offset = (isPointerEvent ? e.pageX : e.touches[0].clientX) - bounds.left;
        offset = Math.max(0, Math.min(offset, totalWidth));
        const _seekPercentage = offset / totalWidth;
        setSeekPercentage(_seekPercentage);
        let seekTime = _seekPercentage * props.duration;
        let seekFrameIndex = -1;
        if (props.onTimelineSeek) {
            if (progressDisplayTimeoutRef.current) {
                window.clearTimeout(progressDisplayTimeoutRef.current);
                progressDisplayTimeoutRef.current = null;
            }
            if (useFrameDisplay && frameRange) {
                const frameDuration = frameRange[1] - frameRange[0] + 1;
                seekFrameIndex = Math.floor(_seekPercentage * frameDuration + frameRange[0]);
            }
            // on Tablet if onTimelineSeek is called directly without this delay of 1ms, the play header
            // index leave a trail and ghost indices.  Almost as if there
            // are too many mouseMove events called and the redraw is not quick enough.
            progressDisplayTimeoutRef.current = window.setTimeout(props.onTimelineSeek, SCRUB_UPDATE_DELAY, seekTime, seekFrameIndex);
        }
    };
    const playHead = (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "timeline__track__playhead", style: {
                left: `${(displayPlayPercentage + oneFramePercent) * 100}%`,
                backgroundColor: `${themeColor}`,
            } }),
        React.createElement("div", { className: "timeline__track__playhead__text", style: {
                left: `${(displayPlayPercentage + oneFramePercent) * 100}%`,
            } }, frameIndex !== -1
            ? frameIndex
            : formatTimeDisplay(displayPlayPercentage * props.duration, props.duration))));
    const playedGauge = (React.createElement("div", { className: "timeline__track__progress", style: {
            background: themeColor,
            width: `${(displayPlayPercentage + oneFramePercent) * 100}%`,
        } }));
    const cachedGauge = props.currentCached && (React.createElement("div", { className: "timeline__track__cache_gauge", style: {
            width: `${(props.currentCached / props.duration + oneFramePercent) * 100}%`,
        } }));
    const markers = props.markerList && (React.createElement(React.Fragment, null, props.markerList.map((t, i) => {
        const markerPercentage = t / props.duration;
        return (React.createElement("div", { key: `timelineMarker${i}`, className: "timeline__track__marker_dot", style: {
                left: `${(markerPercentage + oneFramePercent) * 100}%`,
            } }));
    })));
    return (React.createElement("div", { className: 'timeline' },
        React.createElement("div", { className: "timeline__scrollzone", onPointerMove: handleScrubMove, onTouchMove: handleScrubMove, onPointerDown: handleScrubStart, onTouchStart: handleScrubStart, onPointerUp: handleScrubEnd, onTouchEnd: handleScrubEnd },
            React.createElement("div", { className: "timeline__track" },
                cachedGauge,
                playedGauge,
                playHead,
                markers))));
};

const RPlayer = (_a) => {
    var { seekTime = undefined, onProgressHiFi = undefined } = _a, props = __rest(_a, ["seekTime", "onProgressHiFi"]);
    const reactPlayerRef = useRef(null);
    const syncTimeIdRef = useRef(null);
    const lastBroadcastTimeRef = useRef(null);
    useEffect(() => {
        if (reactPlayerRef.current && seekTime !== undefined && seekTime >= 0) {
            if (reactPlayerRef.current.getInternalPlayer().nodeName === 'VIDEO') {
                reactPlayerRef.current.getInternalPlayer().currentTime = seekTime;
            }
            else {
                reactPlayerRef.current.seekTo(seekTime, 'seconds');
            }
        }
    }, [seekTime]);
    useEffect(() => {
        if (syncTimeIdRef.current) {
            window.clearTimeout(syncTimeIdRef.current);
            syncTimeIdRef.current = null;
        }
        if (onProgressHiFi && reactPlayerRef.current) {
            syncTimeIdRef.current = window.setInterval(() => {
                var _a, _b, _c;
                if (reactPlayerRef.current) {
                    const played = reactPlayerRef.current.getCurrentTime();
                    const loaded = reactPlayerRef.current.getSecondsLoaded();
                    const duration = reactPlayerRef.current.getDuration();
                    if (played !== ((_a = lastBroadcastTimeRef.current) === null || _a === void 0 ? void 0 : _a.played) ||
                        loaded !== ((_b = lastBroadcastTimeRef.current) === null || _b === void 0 ? void 0 : _b.loaded) ||
                        duration !== ((_c = lastBroadcastTimeRef.current) === null || _c === void 0 ? void 0 : _c.duration)) {
                        onProgressHiFi({ loaded, played, duration });
                    }
                    lastBroadcastTimeRef.current = { played, loaded, duration };
                }
            }, 20);
        }
        return () => {
            if (syncTimeIdRef.current) {
                window.clearInterval(syncTimeIdRef.current);
            }
        };
    }, [reactPlayerRef.current]);
    return (React.createElement(ReactPlayer, Object.assign({ ref: reactPlayerRef, style: {
            position: 'absolute',
            top: '0px',
            left: '0px',
            right: '0px',
            bottom: '0px',
            overflow: 'hidden',
        }, width: '100%', height: '100%' }, props)));
};

const Player = (props) => {
    const ctx = React.useContext(PlayerContext);
    const playbackCtx = React.useContext(PlaybackContext);
    const { setContentSize } = React.useContext(PanZoomContext);
    const { type: mediaType } = ctx.mediaList[ctx.mediaIndex];
    useEffect(() => {
        // this normalization is necessary so the text element include in the
        // embed content does not flutuate.
        // Without it, youtube 320x240 will have 2x font size compare to 640x480
        setContentSize(normalizeSize(ctx.mediaList[ctx.mediaIndex], 1920));
    }, [ctx.mediaIndex]);
    const onPlayHandle = useCallback(() => ctx.setPlaying(true), []);
    const onPauseHandle = useCallback(() => ctx.setPlaying(false), []);
    return mediaType === 'image' ? (React.createElement("img", { style: {
            height: '100%',
            width: '100%',
            objectFit: 'contain',
            pointerEvents: 'none',
            userSelect: 'none',
        }, src: ctx.mediaList[ctx.mediaIndex].url })) : (React.createElement(React.Fragment, null,
        React.createElement(RPlayer, { url: ctx.mediaList[ctx.mediaIndex].url, playing: ctx.playing, volume: ctx.muted ? 0 : 1, muted: ctx.muted, onPlay: onPlayHandle, onPause: onPauseHandle, onProgressHiFi: playbackCtx.setPlayerState, seekTime: ctx.seekTime, onEnded: onPauseHandle, controls: props.controls })));
};
const Timeline = () => {
    const ctx = React.useContext(PlayerContext);
    const { playerState } = React.useContext(PlaybackContext);
    const mediaAnnCtx = React.useContext(MediaAnnotateContext);
    const duration = ctx.mediaList[ctx.mediaIndex].duration || playerState.duration;
    const isImage = ctx.mediaList[ctx.mediaIndex].type === 'image';
    const showTimeline = !!duration && !isImage;
    return (React.createElement("div", { style: { display: ctx.seekTimeReady ? undefined : 'none' } }, showTimeline && (React.createElement(Timeline$1, { currentTime: playerState.played, currentCached: playerState.loaded, duration: duration, onTimelineSeek: ctx.setSeekTime, timelineCaptured: ctx.seeking, onTimelineCaptured: ctx.setSeeking, markerList: mediaAnnCtx.annotateTimeList }))));
};
const PlayDeckGradientBackground = () => {
    const { playing } = React.useContext(PlayerContext);
    return (React.createElement("div", { className: `playdeck__shadow playdeck__shadow-${playing ? 'hide' : 'show'}` }));
};
const HoverPlayButton = () => {
    const ctx = React.useContext(PlayerContext);
    const { type: mediaType } = ctx.mediaList[ctx.mediaIndex];
    const showButton = !ctx.seekTimeReady && mediaType !== 'image';
    return (React.createElement("div", { className: `playdeck__play_button playdeck__play_button--${showButton ? 'show' : 'hide'}` },
        React.createElement("div", { className: "playdeck__play_button__button", onClick: () => ctx.setPlaying(true) },
            React.createElement(PlayArrowIcon, { style: { color: '#eee', fontSize: '40px' } }))));
};
const PlayToolbar = () => {
    const ctx = React.useContext(PlayerContext);
    const playbackCtx = React.useContext(PlaybackContext);
    const mediaAnnoCtx = React.useContext(MediaAnnotateContext);
    const markerList = mediaAnnoCtx.annotateTimeList || [];
    const isImage = ctx.mediaList[ctx.mediaIndex].type === 'image';
    return (React.createElement(PlayDeck$1, { iconSize: 'large', 
        //
        playing: ctx.playing, onPlay: () => ctx.setPlaying(!ctx.playing), disablePlay: isImage, 
        //
        muted: ctx.muted, onMuted: () => {
            if (ctx.setMuted)
                ctx.setMuted(!ctx.muted);
        }, disableMute: isImage, 
        //
        fullScreen: ctx.fullScreen, onFullScreen: ctx.setFullScreen, 
        //
        onSkipNext: () => {
            ctx.setMediaIndex((ctx.mediaIndex + 1) % ctx.mediaList.length);
        }, disableNext: ctx.mediaList.length <= 1 || ctx.mediaIndex === ctx.mediaList.length - 1, onSkipPrev: () => {
            ctx.setMediaIndex((ctx.mediaIndex - 1 + ctx.mediaList.length) % ctx.mediaList.length);
        }, disablePrev: ctx.mediaList.length <= 1 || ctx.mediaIndex === 0, 
        //
        onNextAnnotation: () => {
            const nextKey = findNextKeyTime(markerList, playbackCtx.playerState.played);
            if (nextKey !== null)
                ctx.setSeekTime(nextKey);
        }, disableNextAnnotation: !ctx.seekTimeReady || isImage, onPrevAnnotation: () => {
            const prevKey = findPrevKeyTime(markerList, playbackCtx.playerState.played, ctx.playing ? 0.2 : 0);
            if (prevKey !== null)
                ctx.setSeekTime(prevKey);
        }, disablePrevAnnotation: !ctx.seekTimeReady || isImage }));
};
const PlayDeck = () => {
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "playdeck__playbutton_layout" },
            React.createElement(PlayDeckGradientBackground, null),
            React.createElement(HoverPlayButton, null)),
        React.createElement("div", { className: "playdeck__toolbar" },
            React.createElement(Timeline, null),
            React.createElement(PlayToolbar, null))));
};

var playerComposer = /*#__PURE__*/Object.freeze({
    __proto__: null,
    Player: Player,
    Timeline: Timeline,
    PlayToolbar: PlayToolbar,
    PlayDeck: PlayDeck
});

const BrushTools = (props) => {
    const divRef = React.useRef(null);
    usePreventDefaultBrowserTouch(divRef);
    return (React.createElement("div", { className: "brush_tools", ref: divRef },
        React.createElement("div", { className: `brush_tools__display brush_tools__display-${props.hide ? 'hide' : 'show'}` },
            React.createElement("div", { className: "brush_tools_bar" }, props.uiState.shape === 'Textbox' ? (React.createElement(Slider, { value: props.uiState.fontSize, orientation: "vertical", min: 5, max: 100, onChange: (_evt, value) => {
                    props.setUIState(Object.assign(Object.assign({}, props.uiState), { fontSize: value }));
                } })) : (React.createElement(Slider, { orientation: "vertical", value: props.uiState.strokeWidth, onChange: (_evt, value) => {
                    props.setUIState(Object.assign(Object.assign({}, props.uiState), { strokeWidth: value }));
                }, min: 1, max: 50 }))))));
};

const colorPickerStyles = {
    default: {
        picker: {
            background: '#222e',
            backdropFilter: 'blur(3px)',
            borderRadius: '10px 10px 10px 10px',
            border: '0px',
            boxShadow: undefined,
        },
    },
};
const colorResultToString = (v) => {
    return `${v.hex}${Math.round(v.rgb.a * 255)
        .toString(16)
        .padStart(2, '0')}`;
};
const EditTools = (_a) => {
    var { uiState, setUIState, themeColor = '#fff7', iconSize = 'large' } = _a, props = __rest(_a, ["uiState", "setUIState", "themeColor", "iconSize"]);
    const divRef = React.useRef(null);
    usePreventDefaultBrowserTouch(divRef, 'touchmove');
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [tempColor, setTempColor] = useState('');
    const { mode, shape, showAnnotation } = uiState;
    useEffect(() => {
        if (showColorPicker) {
            setTempColor(uiState.color);
        }
    }, [showColorPicker]);
    return (React.createElement("div", { className: "edit_tools", ref: divRef },
        React.createElement("div", { className: `edit_tools__display edit_tools__display-${props.hide ? 'hide' : 'show'}` },
            showColorPicker && (React.createElement("div", { onPointerDown: () => {
                    setShowColorPicker(false);
                }, onTouchStart: () => {
                    setShowColorPicker(false);
                }, style: {
                    pointerEvents: 'auto',
                    position: 'fixed',
                    top: '0px',
                    left: '0px',
                    right: '0px',
                    bottom: '0px',
                } })),
            React.createElement("div", { className: 'edit_tools_bar' },
                React.createElement(IconButton, { size: iconSize, style: { color: showAnnotation ? themeColor : 'red' }, onPointerDown: () => {
                        setUIState(Object.assign(Object.assign({}, uiState), { showAnnotation: !uiState.showAnnotation }));
                    }, title: "Show/Hide Annotation" }, showAnnotation ? React.createElement(Visibility, null) : React.createElement(VisibilityOff, null)),
                props.onAddKey && (React.createElement(IconButton, { size: iconSize, style: {
                        color: themeColor,
                        opacity: props.disableAddKey ? 0.2 : 1.0,
                    }, disabled: props.disableAddKey, onPointerDown: props.onAddKey, title: "Add Key" },
                    React.createElement(AddIcon, null))),
                props.onRemoveKey && (React.createElement(IconButton, { size: iconSize, style: {
                        color: themeColor,
                        opacity: props.disableRemoveKey ? 0.2 : 1.0,
                    }, disabled: props.disableRemoveKey, onPointerDown: props.onRemoveKey, title: "Remove Key" },
                    React.createElement(RemoveIcon, null))),
                props.onDeleteSelection && (React.createElement(IconButton, { size: iconSize, style: {
                        color: themeColor,
                        opacity: props.disableDeleteSelection ? 0.2 : 1.0,
                    }, disabled: props.disableDeleteSelection, onPointerDown: props.onDeleteSelection, title: "Delete Selected" },
                    React.createElement(DeleteIcon, null))),
                props.onCopy && React.createElement("div", { className: "icon_divider" }),
                props.onCopy && (React.createElement(IconButton, { size: iconSize, style: {
                        color: themeColor,
                        opacity: props.disableCopy ? 0.2 : 1.0,
                    }, disabled: props.disableCopy, onPointerDown: props.onCopy, title: "Copy Selected" },
                    React.createElement(CopyIcon, null))),
                props.onPaste && (React.createElement(IconButton, { size: iconSize, style: {
                        color: themeColor,
                        opacity: props.disablePaste ? 0.2 : 1.0,
                    }, disabled: props.disablePaste, onPointerDown: props.onPaste, title: "Paste" },
                    React.createElement(PasteIcon, null))),
                React.createElement("div", { className: "icon_divider" }),
                React.createElement(IconButton, { color: mode === 'selection' ? 'primary' : undefined, size: iconSize, style: { color: mode !== 'selection' ? themeColor : undefined }, onPointerDown: () => {
                        setUIState(Object.assign(Object.assign({}, uiState), { mode: 'selection' }));
                    }, title: "Selection" },
                    React.createElement(HighlightAltOutlined, null)),
                React.createElement(IconButton, { color: mode === 'draw' && shape === 'Path' ? 'primary' : undefined, size: iconSize, style: {
                        color: !(mode === 'draw' && shape === 'Path')
                            ? themeColor
                            : undefined,
                    }, onPointerDown: () => {
                        setUIState(Object.assign(Object.assign({}, uiState), { mode: 'draw', shape: 'Path' }));
                    }, title: "Pen" },
                    React.createElement(Create, null)),
                React.createElement(IconButton, { color: mode === 'draw' && shape === 'Ellipse' ? 'primary' : undefined, style: {
                        color: !(mode === 'draw' && shape === 'Ellipse')
                            ? themeColor
                            : undefined,
                    }, size: iconSize, onPointerDown: () => {
                        setUIState(Object.assign(Object.assign({}, uiState), { mode: 'draw', shape: 'Ellipse' }));
                    }, title: "Ellipse" },
                    React.createElement(CircleOutlined, null)),
                React.createElement(IconButton, { color: mode === 'draw' && shape === 'Rect' ? 'primary' : undefined, style: {
                        color: !(mode === 'draw' && shape === 'Rect')
                            ? themeColor
                            : undefined,
                    }, size: iconSize, onPointerDown: () => {
                        setUIState(Object.assign(Object.assign({}, uiState), { mode: 'draw', shape: 'Rect' }));
                    }, title: "Rectangle" },
                    React.createElement(RectangleOutlined, null)),
                React.createElement(IconButton, { color: mode === 'draw' && shape === 'Textbox' ? 'primary' : undefined, style: {
                        color: !(mode === 'draw' && shape === 'Textbox')
                            ? themeColor
                            : undefined,
                    }, size: iconSize, onPointerDown: () => {
                        setUIState(Object.assign(Object.assign({}, uiState), { mode: 'draw', shape: 'Textbox' }));
                    }, title: "Text" },
                    React.createElement(TextFields, null)),
                React.createElement(IconButton, { style: { color: uiState.color, opacity: 1.0 }, size: iconSize, onPointerDown: () => setShowColorPicker(true), title: "Color" },
                    React.createElement(Circle, null)),
                React.createElement(IconButton, { color: mode === 'panZoom' ? 'primary' : undefined, style: {
                        color: !(mode === 'panZoom') ? themeColor : undefined,
                    }, size: iconSize, onPointerDown: () => {
                        setUIState(Object.assign(Object.assign({}, uiState), { mode: 'panZoom' }));
                    }, title: "Panzoom" },
                    React.createElement(PanToolOutlined, { fontSize: 'medium' })),
                props.onFitScreen && (React.createElement(IconButton, { disabled: props.disableFitScreen, style: {
                        color: themeColor,
                        opacity: props.disableFitScreen ? 0.2 : 1.0,
                    }, size: iconSize, onPointerDown: props.onFitScreen, title: "Fit Content" },
                    React.createElement(FitScreenIcon, null))),
                React.createElement("div", { style: {
                        display: showColorPicker ? 'block' : 'none',
                        position: 'absolute',
                        top: '55px',
                        right: '0px',
                    } },
                    React.createElement(SketchPicker, { color: tempColor, styles: colorPickerStyles, onChange: (v) => {
                            setTempColor(colorResultToString(v));
                        }, onChangeComplete: (v) => {
                            setUIState(Object.assign(Object.assign({}, uiState), { color: colorResultToString(v) }));
                        }, presetColors: [] }))))));
};

const AnnotateCanvas = (props) => {
    const { containerSize, panZoom, contentSize } = useContext(PanZoomContext);
    const { elements, onAddElement, onChangeElement, selection, onSelection } = useContext(MediaAnnotateContext);
    const [normPanZoomSpec, setNormPanZoomSpec] = useState(panZoom);
    // normalize the annotate canvas to have x res regardless of the content
    useEffect(() => setNormPanZoomSpec(Object.assign(Object.assign({}, panZoom), { scale: normalizeScale(contentSize, panZoom.scale, 1280) })), [panZoom]);
    return (React.createElement("div", { style: { display: props.uiState.showAnnotation ? undefined : 'none' } },
        React.createElement(AnnotateCanvas$1, { elements: elements, selection: selection, onSelection: onSelection, width: containerSize.width, height: containerSize.height, panZoom: normPanZoomSpec, uiState: props.uiState, setUiState: props.setUiState, disabled: props.uiState.mode === 'panZoom', onAddElement: onAddElement, onChangeElement: onChangeElement, clearOnElementModify: true })));
};
const AnnotateTools = (props) => {
    const { playing } = useContext(PlayerContext);
    const { playerState } = useContext(PlaybackContext);
    const { isKey, keyTime, onAddKey, onRemoveKey, selection, onRemoveElements, onCopySelected, onPaste, } = useContext(MediaAnnotateContext);
    const { panZoom, setPanZoom, contentFitSpecRef } = useContext(PanZoomContext);
    return (React.createElement(React.Fragment, null,
        React.createElement(BrushTools, { uiState: props.uiState, setUIState: props.setUiState, hide: playing }),
        React.createElement(EditTools, { uiState: props.uiState, setUIState: props.setUiState, 
            //
            onAddKey: onAddKey, disableAddKey: keyTime === getWholeMSecTime(playerState.played), 
            //
            onRemoveKey: onRemoveKey, disableRemoveKey: !isKey, 
            //
            onDeleteSelection: () => onRemoveElements(selection), disableDeleteSelection: selection.length === 0, 
            //
            onCopy: onCopySelected, disableCopy: selection.length === 0, 
            //
            onPaste: onPaste, 
            // disablePaste={}
            //
            onFitScreen: () => {
                setPanZoom(contentFitSpecRef.current.fitSpec);
            }, disableFitScreen: panZoom === contentFitSpecRef.current.fitSpec, hide: playing })));
};

var annotateComposer = /*#__PURE__*/Object.freeze({
    __proto__: null,
    AnnotateCanvas: AnnotateCanvas,
    AnnotateTools: AnnotateTools
});

export { AnnotateCanvas$1 as AnnotateCanvas, MediaAnnotationContextProvider, PanZoomWithContext as PanZoom, PanZoomContainer, PanZoomContent, PanZoomContextProvider, PanZoomOverlay, PlayerContextProvider, annotateComposer, browser, playerComposer };
